import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AudioConversationPreview } from '@/components/audio-conversation-preview';
import { ConversationViewer } from '@/components/conversation-viewer';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  artifactDigestApiClient,
  useRegenerateArtifactDigestMutation,
} from '@/services/api/artifactDigestService';
import {
  artifactApi,
  useGetArtifactUnpackedQuery,
} from '@/services/api/artifactService';
import { useRetriggerBlobTransformMutation } from '@/services/api/blobService';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { BlobPreview } from '../documents-v2/components/inspector/BlobPreview';
import { DigestDebugTab } from './DigestDebugTab';

interface InspectArtifactModalProps {
  artifactId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  matterId?: string;
}

export const InspectArtifactModal = ({
  artifactId,
  isOpen,
  onOpenChange,
  matterId: matterIdProp,
}: InspectArtifactModalProps) => {
  const dispatch = useDispatch();
  const [regenerateDigest, { isLoading: isRegenerating }] =
    useRegenerateArtifactDigestMutation();

  // Pull matterId from route if available, otherwise use prop
  const { matter_id: matterIdFromParams } = useParams<{ matter_id: string }>();
  const matterId = matterIdProp || matterIdFromParams || '';

  // Fetch the unpacked artifact data (includes digest & type)
  const { data: unpackedArtifact, isLoading: isLoadingArtifact } =
    useGetArtifactUnpackedQuery(
      {
        matterId,
        artifactId,
      },
      {
        skip: !artifactId || !matterId,
      },
    );

  const [retriggerTransform, { isLoading: isRetriggering }] =
    useRetriggerBlobTransformMutation();

  const handleRefreshDigest = async () => {
    if (!artifactId) return;

    try {
      // Invalidate the artifact digest cache
      dispatch(
        artifactDigestApiClient.util.invalidateTags([
          { type: 'ArtifactDigests', id: artifactId },
        ]),
      );

      // Invalidate the artifact cache
      dispatch(
        artifactApi.util.invalidateTags([{ type: 'Artifact', id: artifactId }]),
      );

      toast.success('Artifact digest refreshed');
    } catch (error) {
      toast.error('Failed to refresh artifact digest');
    }
  };

  const handleRegenerateDigest = async () => {
    if (!artifactId) return;

    try {
      await regenerateDigest(artifactId).unwrap();
      toast.success('Artifact digest regeneration initiated');
    } catch (error) {
      toast.error('Failed to regenerate artifact digest');
    }
  };

  const handleRetriggerTransform = async () => {
    if (
      !unpackedArtifact ||
      unpackedArtifact.artifact_type !== ArtifactType.BLOB
    )
      return;
    const matterIdParam = unpackedArtifact.matter_id;
    const blobId = unpackedArtifact.blob.id;
    try {
      await retriggerTransform({ matterId: matterIdParam, blobId }).unwrap();
      toast.success('Transform retriggered');
      // Invalidate artifact cache to refresh UI
      dispatch(
        artifactApi.util.invalidateTags([{ type: 'Artifact', id: artifactId }]),
      );
    } catch (error: any) {
      const message = error?.data?.error || 'Failed to retrigger transform';
      toast.error(message);
    }
  };

  const renderPreviewContent = () => {
    if (isLoadingArtifact) {
      return (
        <div className="p-4 text-center">
          <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">Loading artifact data...</p>
        </div>
      );
    }

    if (!unpackedArtifact) {
      return (
        <div className="p-4 text-center text-muted-foreground">
          No preview available
        </div>
      );
    }

    if (
      unpackedArtifact.artifact_type === ArtifactType.BLOB &&
      unpackedArtifact.blob
    ) {
      return <BlobPreview file={unpackedArtifact.blob} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.AUDIO_CONVERSATION) {
      return <AudioConversationPreview artifact={unpackedArtifact} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.TRANSCRIPT) {
      return <AudioConversationPreview artifact={unpackedArtifact} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.CONVERSATION) {
      return <ConversationViewer artifact={unpackedArtifact} />;
    }

    return (
      <div className="p-4 text-center text-muted-foreground">
        No preview available for this artifact type
      </div>
    );
  };

  const renderDebugContent = () => {
    if (isLoadingArtifact) {
      return (
        <div className="p-4 text-center">
          <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">Loading artifact digest...</p>
        </div>
      );
    }

    if (!unpackedArtifact) {
      return (
        <div className="p-4 text-center text-muted-foreground">
          No artifact information available
        </div>
      );
    }

    return <DigestDebugTab unpackedArtifact={unpackedArtifact} />;
  };

  return (
    <Dialog open={isOpen} onClose={onOpenChange} size="6xl" zIndex={110} nested>
      <DialogTitle className="flex items-center justify-between">
        <span>Artifact Details</span>
        {artifactId && (
          <div className="flex gap-2">
            <Button
              onClick={handleRefreshDigest}
              disabled={isLoadingArtifact}
              aria-label="Refresh Digest"
              outline
            >
              <ArrowPathIcon
                className={`h-4 w-4 mr-2 ${isLoadingArtifact ? 'animate-spin' : ''}`}
              />
              Refresh Digest
            </Button>
            <Button
              onClick={handleRegenerateDigest}
              disabled={isRegenerating}
              aria-label="Regenerate Digest"
            >
              <ArrowPathIcon
                className={`h-4 w-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`}
              />
              Regenerate Digest
            </Button>
            {unpackedArtifact?.artifact_type === ArtifactType.BLOB && (
              <Button
                onClick={handleRetriggerTransform}
                disabled={isRetriggering}
                aria-label="Retrigger Transform"
                outline
              >
                <ArrowPathIcon
                  className={`h-4 w-4 mr-2 ${isRetriggering ? 'animate-spin' : ''}`}
                />
                Retrigger Transform
              </Button>
            )}
          </div>
        )}
      </DialogTitle>
      <DialogBody className="h-[85vh] p-0 overflow-hidden">
        <div className="flex h-full overflow-hidden">
          {/* Preview panel on left (65%) */}
          <div className="w-[65%] border-r overflow-auto p-4 h-full">
            {renderPreviewContent()}
          </div>

          {/* Debug information on right (35%) */}
          <div className="w-[35%] overflow-auto p-4 h-full">
            {renderDebugContent()}
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
};
